import React, { useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router';
import { Icon } from '@fuxi/eevee-icon';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentServingOrder } from '@/store/service';
import logoImg from '@/assets/image/home-logo.png';
import { useNav } from '@/hooks';
import service from '@/service';
import { handleNavFromOrderInfo, step2Url } from '@/utils';
import { selectedIsCanClickTabBar, selectedIsCanNavToMassageControl, selectedStoreInfo } from '@/store/device';
import cx from './index.module.scss';

interface Props {}

const TabBar: React.FC<Props> = () => {
  const location = useLocation();
  const isCanClickTabBar = useSelector(selectedIsCanClickTabBar);
  const isCanNavToMassageControl = useSelector(selectedIsCanNavToMassageControl);
  const nav = useNav();
  const isClickedCurrentBtn = useRef(false);
  const dispatch = useDispatch();
  const storeInfo = useSelector(selectedStoreInfo);

  const currentTab = useMemo(() => location?.pathname.replace(/\//g, '') as TabName, [location?.pathname]);
  const isShowTabBar = useMemo(() => !['/login', '/device-binding'].includes(location.pathname), [location?.pathname]);
  const isSelectCurrentServicePage = useMemo(
    () =>
      ![
        'home',
        'devicemanagement',
        'pad-info',
        'devicepos-assist',
        'newdevicepos-assist',
        'service-record',
        'service-record-detail',
        'helphome',
      ].includes(currentTab),
    [currentTab]
  );

  const isSelectDeviceMgntPage = useMemo(
    () => ['devicemanagement', 'devicepos-assist', 'newdevicepos-assist'].includes(currentTab),
    [currentTab]
  );

  const tempNav = useCallback(
    (url: string) => () => {
      if (!isCanClickTabBar) return;
      if (url === location.pathname) return;

      nav(url, { replace: true });
    },
    [isCanClickTabBar, location.pathname, nav]
  );

  const handleNav2CurrentService = useCallback(async () => {
    if (!isCanClickTabBar) return;
    if (
      ['/service/massage-control', '/new/service/massage-control', '/service/confirm-plan'].includes(location.pathname)
    ) {
      return;
    }
    if (isClickedCurrentBtn.current) return;

    isClickedCurrentBtn.current = true;

    try {
      const { data } = await service.service.getServingOrderByOperator();
      dispatch(setCurrentServingOrder(data));
      if (data?.step) {
        if (step2Url[data?.step] === location.pathname) return;

        if (data.step === 'RoboticTask' && !isCanNavToMassageControl) {
          return;
        }

        handleNavFromOrderInfo(data, nav);
      } else {
        if (location.pathname === '/no-service') return;

        nav('/no-service');
      }
    } finally {
      isClickedCurrentBtn.current = false;
    }
  }, [dispatch, isCanClickTabBar, isCanNavToMassageControl, location.pathname, nav]);

  return (
    <div className={cx('tab-bar-container')}>
      <div className={cx('tab-bar', { show: isShowTabBar })}>
        <div className={cx('tab-item', { selected: isSelectDeviceMgntPage })}>
          <div className={cx('inner-box')} onClick={tempNav('/device/management')}>
            <Icon name="tab-device" className={cx('device-icon', 'icon')} />
            {/* <img src={DeviceIcon} alt="device" className={cx('device-icon', 'icon')}></img> */}
            <div>设备</div>
          </div>
        </div>
        <div className={cx('tab-item', { selected: currentTab === 'home' })}>
          <div className={cx('inner-box')} onClick={tempNav('/home')}>
            <Icon name="tab-home" className={cx('icon')} />
            <div>首页</div>
          </div>
        </div>
        <div className={cx('tab-item', { selected: isSelectCurrentServicePage })}>
          <div className={cx('inner-box')} onClick={handleNav2CurrentService}>
            <Icon name="tab-serve" className={cx('icon')} />
            <div>服务</div>
          </div>
        </div>
        <div className={cx('tab-item', { selected: currentTab === 'helphome' })}>
          <div className={cx('inner-box')} onClick={tempNav('/help/home')}>
            <Icon name="tab-info" className={cx('icon')} />
            <div>帮助</div>
          </div>
        </div>
        <div className={cx('tab-bottom-item')}>
          <div
            className={cx('service-detail-icon', {
              selected: ['service-record', 'service-record-detail']?.includes(currentTab),
            })}
          >
            <div className={cx('inner-box')} onClick={tempNav('/service-record')}>
              <Icon name="folder-open" className={cx('icon')} />
            </div>
          </div>
          <div
            className={cx('text', {
              'text-selected': ['service-record', 'service-record-detail']?.includes(currentTab),
            })}
          >
            服务详情
          </div>
        </div>
      </div>
      <div className={cx('logo-wrapper')}>
        {storeInfo?.logoImage ? (
          <div className={cx('has-logo')}>
            <img src={storeInfo?.logoImage} alt="logo-img" />
          </div>
        ) : (
          <>
            <div className={cx('img-wrapper')}>
              <img src={logoImg} alt="" />
            </div>
            <div className={cx('info-text')}>商家专业版</div>
          </>
        )}
      </div>
    </div>
  );
};

export default TabBar;
